import { Section } from "../../../components/Section";
import { MaxLoanAmountsDebug } from "../../../components/MaxLoanAmountsDebug";
import { formatCurrency, formatCurrencyWhole, formatPercentage, formatMultiple } from "../../../utils/formatters";

interface MortgageResultsSectionProps {
    mortgageCalculations: any;
    totalCostsCalculations: any;
    formData?: any;
    householdExpenditure?: any;
    maxLoanData?: any;
    isAdmin?: boolean;
    isLoadingExistingData?: boolean;
}

export function MortgageResultsSection({
                                           mortgageCalculations,
                                           totalCostsCalculations,
                                           formData,
                                           householdExpenditure,
                                           maxLoanData,
                                           isAdmin = false,
                                           isLoadingExistingData = false
                                       }: MortgageResultsSectionProps) {

    if (!mortgageCalculations || !totalCostsCalculations) {
        return (
            <Section title="Mortgage Calculation Results">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan"></div>
                </div>
            </Section>
        );
    }

    const calculations = mortgageCalculations.calculations;
    const details = mortgageCalculations.calculationDetails;

    // Helper function to get display amount and check for caps
    const getDisplayAmount = (amount: number) => {
        const MIN_LOAN = 20000000; // £200,000 in pennies
        const MAX_LOAN = 500000000; // £5,000,000 in pennies

        // Round DOWN to nearest pound (100 pennies)
        const roundedAmount = Math.floor(amount / 100) * 100;

        if (roundedAmount > MAX_LOAN) {
            return { displayAmount: MAX_LOAN, hitCap: true, belowMin: false };
        }

        return {
            displayAmount: roundedAmount,
            hitCap: false,
            belowMin: roundedAmount > 0 && roundedAmount < MIN_LOAN
        };
    };

    return (
        <Section title="Mortgage Calculation Results" id="mortgage-results">
            <div className="space-y-8">

                {/* Max Loan Results - Highlighted Section */}
                {maxLoanData && (
                    <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 p-6 rounded-xl border-2 border-cyan-200 dark:border-cyan-700 shadow-lg">
                        
                        {/* Informational text */}
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-lg border border-white/20 dark:border-gray-600/30 mb-6 shadow-sm">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                    We have provided an indication for both 5 year fixed and other products, using the interest rate you provided above. You can find all our rates in our{' '}
                                    <a
                                        href="https://mhbs.co.uk/document/mhbs-intermediary-product-guide/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader transition-colors"
                                    >
                                        Product Guide
                                    </a>
                                </p>
                            </div>
                        </div>
                        
                        <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-200 mb-6 text-center">Maximum Loan Amounts</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Max Loan 5 Year Fixed */}
                            <div className={`p-6 rounded-lg border-2 shadow-md ${
                                (() => {
                                    const { belowMin } = getDisplayAmount(maxLoanData.maxLoan5YearFixed);
                                    if (belowMin) {
                                        return 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700';
                                    }
                                    return maxLoanData.maxLoan5YearFixed > 0
                                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700';
                                })()
                            }`}>
                                <h4 className={`text-lg font-subheader mb-3 ${
                                    (() => {
                                        const { belowMin } = getDisplayAmount(maxLoanData.maxLoan5YearFixed);
                                        if (belowMin) {
                                            return 'text-red-800 dark:text-red-300';
                                        }
                                        return maxLoanData.maxLoan5YearFixed > 0 ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300';
                                    })()
                                }`}>
                                    Max Loan 5 Year Fixed
                                </h4>
                                <div className={`text-3xl font-bold mb-2 ${
                                    (() => {
                                        const { belowMin } = getDisplayAmount(maxLoanData.maxLoan5YearFixed);
                                        if (belowMin) {
                                            return 'text-red-700 dark:text-red-400';
                                        }
                                        return maxLoanData.maxLoan5YearFixed > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400';
                                    })()
                                }`}>
                                    {(() => {
                                        const { displayAmount, hitCap, belowMin } = getDisplayAmount(maxLoanData.maxLoan5YearFixed);
                                        return formatCurrencyWhole(displayAmount);
                                    })()}
                                </div>


                                {/* Health Warnings */}
                                {(() => {
                                    const { hitCap, belowMin } = getDisplayAmount(maxLoanData.maxLoan5YearFixed);
                                    if (hitCap) {
                                        return (
                                            <div className="mt-3 p-2 bg-orange-100 dark:bg-orange-800/30 rounded border border-orange-300 dark:border-orange-600">
                                                <p className="text-xs text-orange-800 dark:text-orange-200 font-subheader">
                                                    ⚠️ Maximum loan cap of £5,000,000 reached
                                                </p>
                                            </div>
                                        );
                                    }
                                    if (belowMin) {
                                        return (
                                            <div className="mt-3 p-2 bg-red-100 dark:bg-red-800/30 rounded border border-red-300 dark:border-red-600">
                                                <p className="text-xs text-red-800 dark:text-red-200 font-subheader">
                                                    ⚠️ Minimum loan amount of £200,000 cannot be reached
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>

                            {/* Max Loan Other */}
                            <div className={`p-6 rounded-lg border-2 shadow-md ${
                                (() => {
                                    const { belowMin } = getDisplayAmount(maxLoanData.maxLoanOther);
                                    if (belowMin) {
                                        return 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700';
                                    }
                                    return maxLoanData.maxLoanOther > 0
                                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700';
                                })()
                            }`}>
                                <h4 className={`text-lg font-subheader mb-3 ${
                                    (() => {
                                        const { belowMin } = getDisplayAmount(maxLoanData.maxLoanOther);
                                        if (belowMin) {
                                            return 'text-red-800 dark:text-red-300';
                                        }
                                        return maxLoanData.maxLoanOther > 0 ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300';
                                    })()
                                }`}>
                                    Max Loan Other
                                </h4>
                                <div className={`text-3xl font-bold mb-2 ${
                                    (() => {
                                        const { belowMin } = getDisplayAmount(maxLoanData.maxLoanOther);
                                        if (belowMin) {
                                            return 'text-red-700 dark:text-red-400';
                                        }
                                        return maxLoanData.maxLoanOther > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400';
                                    })()
                                }`}>
                                    {(() => {
                                        const { displayAmount, hitCap, belowMin } = getDisplayAmount(maxLoanData.maxLoanOther);
                                        return formatCurrencyWhole(displayAmount);
                                    })()}
                                </div>


                                {/* Health Warnings */}
                                {(() => {
                                    const { hitCap, belowMin } = getDisplayAmount(maxLoanData.maxLoanOther);
                                    if (hitCap) {
                                        return (
                                            <div className="mt-3 p-2 bg-orange-100 dark:bg-orange-800/30 rounded border border-orange-300 dark:border-orange-600">
                                                <p className="text-xs text-orange-800 dark:text-orange-200 font-subheader">
                                                    ⚠️ Maximum loan cap of £5,000,000 reached
                                                </p>
                                            </div>
                                        );
                                    }
                                    if (belowMin) {
                                        return (
                                            <div className="mt-3 p-2 bg-red-100 dark:bg-red-800/30 rounded border border-red-300 dark:border-red-600">
                                                <p className="text-xs text-red-800 dark:text-red-200 font-subheader">
                                                    ⚠️ Minimum loan amount of £200,000 cannot be reached
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>

                        <div className="mt-4 text-center text-xs text-green-600 dark:text-green-400">
                            ✓ Results automatically saved
                        </div>
                        
                        {/* Contact Information */}
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-lg border border-white/20 dark:border-gray-600/30 mt-4 shadow-sm">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                    If you'd like to talk your case through in more detail, we'll be happy to help. Please contact your nearest{' '}
                                    <a
                                        href="https://mhbs.co.uk/intermediaries/contact-us/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader transition-colors"
                                    >
                                        BDM
                                    </a>
                                    {' '}or call 01858412345.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Section>
    );
}
